import { useState, useMemo } from "react";
import useSWR from 'swr';
import TabPageLayout, { PageLoader, DataCard } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { 
  Building2, 
  Clock, 
  CheckCircle, 
  ExternalLink,
  RefreshCw,
  Calendar,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  ChevronDown,
  AlertCircle,
  ShoppingCart,
  RotateCcw,
  TrendingUp
} from "lucide-react";
import { BACKEND_URL } from '../config/api';
import axios from "axios";
import { toast } from "sonner";
import jsPDF from 'jspdf';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";

const PLATFORM_API = `${BACKEND_URL}/api/platform`;

// Fetcher for SWR
const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Currency formatting helper
const formatCurrency = (amount, currency = 'USD') => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num);
};

// Big Stat Card Component - More square proportions
const StatCard = ({ label, value, subtext, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'bg-white border-gray-200',
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200',
  };
  
  const textVariants = {
    default: { label: 'text-gray-600', value: 'text-gray-900', sub: 'text-gray-500' },
    green: { label: 'text-green-700', value: 'text-green-800', sub: 'text-green-600' },
    blue: { label: 'text-blue-700', value: 'text-blue-800', sub: 'text-blue-600' },
    amber: { label: 'text-amber-700', value: 'text-amber-800', sub: 'text-amber-600' },
    red: { label: 'text-red-700', value: 'text-red-800', sub: 'text-red-600' },
    purple: { label: 'text-purple-700', value: 'text-purple-800', sub: 'text-purple-600' },
  };

  const colors = textVariants[variant] || textVariants.default;
  
  return (
    <div className={`${variants[variant]} border rounded-xl p-5 aspect-[4/3] flex flex-col justify-between`}>
      <div className="flex items-start justify-between">
        <p className={`text-sm font-medium ${colors.label}`}>{label}</p>
        {Icon && (
          <div className={`p-2 rounded-lg ${variant === 'default' ? 'bg-gray-100' : 'bg-white/50'}`}>
            <Icon className={`w-4 h-4 ${colors.label}`} />
          </div>
        )}
      </div>
      <div>
        <p className={`text-2xl sm:text-3xl font-bold ${colors.value}`}>{value}</p>
        {subtext && <p className={`text-xs mt-1 ${colors.sub}`}>{subtext}</p>}
      </div>
    </div>
  );
};

// Custom tooltip for chart
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
        <p className="font-medium text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SuperAdminDashboard = () => {
  // Date range state - default to last 6 months for better chart view
  const [startDate, setStartDate] = useState(() => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return sixMonthsAgo.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Quick date presets
  const applyPreset = (preset) => {
    const now = new Date();
    let start, end = now.toISOString().split('T')[0];
    
    switch(preset) {
      case 'today':
        start = end;
        break;
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        break;
      case '3months':
        start = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
        break;
      case '6months':
        start = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      case 'all':
        start = '2020-01-01';
        break;
      default:
        return;
    }
    
    setStartDate(start);
    setEndDate(end);
    setDatePickerOpen(false);
  };

  // Fetch organizations with SWR
  const { data: orgsData, error: orgsError, mutate: mutateOrgs } = useSWR(
    `${PLATFORM_API}/organizations`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 }
  );

  // Fetch financials with SWR using date range
  const { data: financials, error: financialsError, mutate: mutateFinancials } = useSWR(
    `${BACKEND_URL}/api/super-admin/platform-financials?start_date=${startDate}&end_date=${endDate}`,
    fetcher,
    { revalidateOnFocus: true }
  );

  // Calculate stats from orgs
  const stats = useMemo(() => {
    const orgs = orgsData || [];
    const active = orgs.filter(o => o.status === 'active' || o.status === 'approved').length;
    const pending = orgs.filter(o => o.status === 'pending').length;
    const suspended = orgs.filter(o => o.status === 'suspended').length;
    const recentSignups = [...orgs]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);
      
    return {
      totalOrgs: orgs.length,
      activeOrgs: active,
      pendingOrgs: pending,
      suspendedOrgs: suspended,
      recentSignups,
      allOrgs: orgs
    };
  }, [orgsData]);

  const isLoading = !orgsData && !orgsError;

  // GMV Calculations
  const gmvMetrics = useMemo(() => {
    if (!financials?.summary) return null;
    
    const grossGMV = financials.summary.grossRevenue || financials.summary.totalRevenue || 0;
    const refunds = financials.summary.totalRefunds || 0;
    const netGMV = grossGMV - refunds;
    const platformFees = financials.summary.platformFees || 0;
    const totalBookings = financials.summary.totalBookings || 0;
    const refundCount = financials.summary.refundCount || 0;
    
    return {
      grossGMV,
      refunds,
      netGMV,
      platformFees,
      totalBookings,
      refundCount,
      avgBookingValue: totalBookings > 0 ? grossGMV / totalBookings : 0,
      avgPlatformFee: totalBookings > 0 ? platformFees / totalBookings : 0,
    };
  }, [financials]);

  // Pie chart data
  const pieData = useMemo(() => {
    if (!gmvMetrics) return [];
    const operatorRevenue = gmvMetrics.grossGMV - gmvMetrics.platformFees;
    return [
      { name: 'Operators', value: operatorRevenue, color: '#10B981' },
      { name: 'Platform', value: gmvMetrics.platformFees, color: '#2563EB' }
    ];
  }, [gmvMetrics]);

  // Export JSON
  const exportJSON = () => {
    const data = {
      reportInfo: {
        generatedAt: new Date().toISOString(),
        dateRange: { start: startDate, end: endDate },
        generatedBy: 'BookingFlows Super Admin'
      },
      financialSummary: {
        grossGMV: gmvMetrics?.grossGMV || 0,
        totalRefunds: gmvMetrics?.refunds || 0,
        netGMV: gmvMetrics?.netGMV || 0,
        platformRevenue: gmvMetrics?.platformFees || 0,
        totalBookings: gmvMetrics?.totalBookings || 0,
        refundCount: gmvMetrics?.refundCount || 0,
        avgBookingValue: gmvMetrics?.avgBookingValue || 0,
        avgPlatformFee: gmvMetrics?.avgPlatformFee || 0,
        currency: financials?.summary?.currency || 'USD'
      },
      organizationStats: {
        total: stats.totalOrgs,
        active: stats.activeOrgs,
        pending: stats.pendingOrgs,
        suspended: stats.suspendedOrgs
      },
      organizationBreakdown: financials?.topOrganizations?.map(org => ({
        name: org.name,
        slug: org.slug,
        gmv: org.revenue,
        platformFee: org.fees,
        bookings: org.bookings,
        currency: org.currency || 'USD'
      })) || [],
      monthlyBreakdown: financials?.monthlyChart || [],
      recentTransactions: financials?.recentFees || []
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookingflows-report-${startDate}-to-${endDate}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported as JSON");
  };

  // Export CSV with detailed breakdown
  const exportCSV = () => {
    const rows = [
      ['BOOKINGFLOWS FINANCIAL REPORT'],
      [`Date Range: ${startDate} to ${endDate}`],
      [`Generated: ${new Date().toISOString()}`],
      [''],
      ['FINANCIAL SUMMARY'],
      ['Metric', 'Value', 'Notes'],
      ['Gross GMV', (gmvMetrics?.grossGMV || 0).toFixed(2), 'Total processed - matches Stripe Volume'],
      ['Refunded GMV', (gmvMetrics?.refunds || 0).toFixed(2), 'Processing fees still kept'],
      ['Net GMV', (gmvMetrics?.netGMV || 0).toFixed(2), 'Gross minus refunds'],
      ['Platform Revenue', (gmvMetrics?.platformFees || 0).toFixed(2), 'BookingFlows earnings'],
      ['Total Bookings', gmvMetrics?.totalBookings || 0, ''],
      ['Refund Count', gmvMetrics?.refundCount || 0, ''],
      ['Avg Booking Value', (gmvMetrics?.avgBookingValue || 0).toFixed(2), ''],
      ['Avg Platform Fee', (gmvMetrics?.avgPlatformFee || 0).toFixed(2), ''],
      [''],
      ['ORGANIZATION BREAKDOWN'],
      ['Name', 'Slug', 'GMV', 'Platform Fee', 'Bookings', 'Avg Booking'],
    ];
    
    // Add org breakdown
    (financials?.topOrganizations || []).forEach(org => {
      const avgBooking = org.bookings > 0 ? (org.revenue / org.bookings).toFixed(2) : 0;
      rows.push([`"${org.name}"`, org.slug, org.revenue?.toFixed(2), org.fees?.toFixed(2), org.bookings, avgBooking]);
    });
    
    // Monthly breakdown
    if (financials?.monthlyChart?.length) {
      rows.push([''], ['MONTHLY BREAKDOWN'], ['Month', 'GMV', 'Platform Fee']);
      financials.monthlyChart.forEach(m => {
        rows.push([m.month, m.revenue?.toFixed(2), m.fees?.toFixed(2)]);
      });
    }
    
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookingflows-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported as CSV");
  };

  // Export PDF - Official Document
  const exportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('BookingFlows Financial Report', pageWidth / 2, y, { align: 'center' });
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`${startDate} to ${endDate}`, pageWidth / 2, y, { align: 'center' });
    y += 5;
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, y, { align: 'center' });
    y += 15;
    
    // Financial Summary Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Financial Summary', 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const summaryData = [
      ['Gross GMV', formatCurrency(gmvMetrics?.grossGMV), 'Total processed volume'],
      ['Platform Revenue', formatCurrency(gmvMetrics?.platformFees), 'BookingFlows earnings'],
      ['Total Bookings', (gmvMetrics?.totalBookings || 0).toString(), 'Confirmed orders'],
      ['Refunded GMV', formatCurrency(gmvMetrics?.refunds), 'Processing fees kept'],
      ['Net GMV', formatCurrency(gmvMetrics?.netGMV), 'After refunds'],
    ];
    
    summaryData.forEach(([label, value, note]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label + ':', 20, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 70, y);
      doc.setTextColor(100);
      doc.text(note, 120, y);
      doc.setTextColor(0);
      y += 7;
    });
    
    y += 10;
    
    // Organization Breakdown
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Organization Breakdown', 20, y);
    y += 10;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Organization', 20, y);
    doc.text('GMV', 90, y);
    doc.text('Platform Fee', 120, y);
    doc.text('Bookings', 160, y);
    y += 5;
    
    doc.setFont('helvetica', 'normal');
    (financials?.topOrganizations || []).slice(0, 10).forEach(org => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(org.name?.substring(0, 30) || '', 20, y);
      doc.text(formatCurrency(org.revenue), 90, y);
      doc.text(formatCurrency(org.fees), 120, y);
      doc.text((org.bookings || 0).toString(), 160, y);
      y += 6;
    });
    
    y += 10;
    
    // Organization Stats
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Platform Statistics', 20, y);
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Organizations: ${stats.totalOrgs}`, 20, y); y += 6;
    doc.text(`Active: ${stats.activeOrgs}`, 20, y); y += 6;
    doc.text(`Pending: ${stats.pendingOrgs}`, 20, y); y += 6;
    doc.text(`Suspended: ${stats.suspendedOrgs}`, 20, y);
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
      doc.text('BookingFlows - Confidential', 20, 290);
    }
    
    doc.save(`bookingflows-report-${startDate}-to-${endDate}.pdf`);
    toast.success("PDF report exported");
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      suspended: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  const handleRefresh = () => {
    mutateOrgs();
    mutateFinancials();
    toast.success("Refreshing data...");
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  return (
    <TabPageLayout
      title="Platform Overview"
      subtitle={`${formatDateShort(startDate)} - ${formatDateShort(endDate)}`}
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Range Picker */}
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 gap-2">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">{formatDateShort(startDate)} - {formatDateShort(endDate)}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end">
              <div className="space-y-4">
                <div className="text-sm font-medium text-gray-900">Select Date Range</div>
                
                {/* Quick Presets */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'week', label: '7 Days' },
                    { key: 'month', label: 'This Month' },
                    { key: '3months', label: '3 Months' },
                    { key: '6months', label: '6 Months' },
                    { key: 'year', label: 'This Year' },
                    { key: 'all', label: 'All Time' },
                  ].map(preset => (
                    <button
                      key={preset.key}
                      onClick={() => applyPreset(preset.key)}
                      className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                
                {/* Custom Date Inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Start Date</label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">End Date</label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={() => setDatePickerOpen(false)} 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9"
                >
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="sm" onClick={handleRefresh} className="h-9">
            <RefreshCw className="w-4 h-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportPDF}>
                <FileText className="w-4 h-4 mr-2" />PDF Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportCSV}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />CSV Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportJSON}>
                <FileJson className="w-4 h-4 mr-2" />JSON Data
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Key Financial Metrics - 4 square cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Gross GMV"
            value={formatCurrency(gmvMetrics?.grossGMV, financials?.summary?.currency)}
            subtext={`${(gmvMetrics?.totalBookings || 0).toLocaleString()} bookings`}
            icon={ShoppingCart}
            variant="green"
          />
          <StatCard
            label="Platform Revenue"
            value={formatCurrency(gmvMetrics?.platformFees, financials?.summary?.currency)}
            subtext="BookingFlows earnings"
            icon={TrendingUp}
            variant="blue"
          />
          <StatCard
            label="Total Bookings"
            value={(gmvMetrics?.totalBookings || 0).toLocaleString()}
            subtext={`Avg ${formatCurrency(gmvMetrics?.avgBookingValue)}`}
            icon={CheckCircle}
            variant="purple"
          />
          {(gmvMetrics?.refunds || 0) > 0 ? (
            <StatCard
              label="Refunded GMV"
              value={formatCurrency(gmvMetrics?.refunds, financials?.summary?.currency)}
              subtext="Processing fees still kept"
              icon={RotateCcw}
              variant="red"
            />
          ) : (
            <StatCard
              label="Avg Platform Fee"
              value={formatCurrency(gmvMetrics?.avgPlatformFee, financials?.summary?.currency)}
              subtext="Per booking"
              icon={Building2}
              variant="default"
            />
          )}
        </div>

        {/* Revenue Trend Chart - Full Width */}
        <DataCard title="Revenue Trend" subtitle="Monthly GMV and Platform Revenue">
          <div className="h-64 sm:h-80">
            {financials?.monthlyChart?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={financials.monthlyChart} 
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonth}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    tickFormatter={(val) => `$${val >= 1000 ? Math.round(val/1000) + 'k' : val}`}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '10px' }}
                  />
                  <Bar 
                    dataKey="revenue" 
                    name="GMV" 
                    fill="#10B981" 
                    radius={[4, 4, 0, 0]} 
                    maxBarSize={60}
                  />
                  <Bar 
                    dataKey="fees" 
                    name="Platform Fee" 
                    fill="#2563EB" 
                    radius={[4, 4, 0, 0]} 
                    maxBarSize={60}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No data for this period
              </div>
            )}
          </div>
        </DataCard>

        {/* Two Column: Revenue Split + Org Stats Combined */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Split Pie + Org Stats */}
          <DataCard title="Revenue Split & Organization Stats">
            <div className="grid grid-cols-2 gap-4">
              {/* Pie Chart */}
              <div>
                <div className="h-[160px]">
                  {pieData.length > 0 && pieData[0].value > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
                <div className="flex justify-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-500">Operators</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                    <span className="text-xs text-gray-500">Platform</span>
                  </div>
                </div>
              </div>
              
              {/* Org Stats */}
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Total Orgs</span>
                  <span className="font-semibold text-gray-900">{stats.totalOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-green-600">Active</span>
                  <span className="font-semibold text-green-700">{stats.activeOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-amber-600">Pending</span>
                  <span className="font-semibold text-amber-700">{stats.pendingOrgs}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-red-600">Suspended</span>
                  <span className="font-semibold text-red-700">{stats.suspendedOrgs}</span>
                </div>
              </div>
            </div>
          </DataCard>

          {/* Recent Signups */}
          <DataCard title="Recent Signups">
            <div className="space-y-3">
              {stats.recentSignups.length > 0 ? (
                stats.recentSignups.map((org) => (
                  <div key={org.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm truncate">{org.name}</p>
                      <p className="text-xs text-gray-400 truncate">{org.ownerEmail}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(org.status)}`}>
                      {org.status}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No recent signups</p>
              )}
            </div>
          </DataCard>
        </div>

        {/* Top Organizations */}
        <DataCard title="Top Organizations by GMV">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">#</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Organization</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">GMV</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Platform Fee</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Bookings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {financials?.topOrganizations?.slice(0, 10).map((org, index) => (
                  <tr key={org.slug} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{org.name}</p>
                      <p className="text-xs text-gray-400">{org.slug}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatCurrency(org.revenue, org.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-600">
                      {formatCurrency(org.fees, org.currency)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {org.bookings}
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      No revenue data for this period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DataCard>

        {/* Stripe Link */}
        <DataCard>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="font-semibold text-gray-900">Stripe Dashboard</p>
              <p className="text-sm text-gray-500">View payouts, balance & verify GMV against Stripe Volume</p>
            </div>
            <Button
              onClick={() => window.open('https://dashboard.stripe.com/', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-full sm:w-auto"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Stripe
            </Button>
          </div>
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminDashboard;
